import type { Control, FieldValues, Path } from 'react-hook-form';

import type { FormField as DynamicFormField } from '@/types';
import { FormAutoCompleteField } from './form-auto-complete-field';
import { FormCheckboxGroupField } from './form-checkbox-group-field';
import { FormWheelDatePickerField } from './form-wheel-date-picker-field';
import { FormImageUploadField } from './form-image-upload-field';
import { FormInputField } from './form-input-field';
import { FormPhoneField } from './form-phone-field';
import { FormSelectField } from './form-select-field';
import { FormTextareaField } from './form-textarea-field';
import { isPhoneField } from '@/lib/dynamic-form-schema';

interface DynamicFormRendererProps<TFieldValues extends FieldValues> {
  fields: DynamicFormField[];
  control: Control<TFieldValues>;
  /** Folder passed to `/api/upload` for `IMAGE` fields. */
  uploadFolder: string;
  /** Parent can observe image upload state to gate its submit button. */
  onUploadingChange?: (uploading: boolean) => void;
  /**
   * Optional inline helper rendered directly beneath a specific field. Use
   * for transient messages like "checking phone uniqueness…".
   */
  renderFieldFooter?: (field: DynamicFormField) => React.ReactNode;
  /** Autocomplete threshold for `SELECT` fields. Defaults to 5 options. */
  autocompleteThreshold?: number;
}

/**
 * Renders a list of user-authored form fields (from `FormField[]`) bound to an
 * existing `react-hook-form` control. The consumer is responsible for
 * providing `useForm` + validation (usually via `buildDynamicZodSchema`) and
 * for wrapping the renderer in its own `<Form>` + `<form>` tags.
 */
export function DynamicFormRenderer<TFieldValues extends FieldValues>({
  fields,
  control,
  uploadFolder,
  onUploadingChange,
  renderFieldFooter,
  autocompleteThreshold = 5,
}: DynamicFormRendererProps<TFieldValues>) {
  return (
    <div className="space-y-6">
      {fields.map((field) => {
        const name = field.id as Path<TFieldValues>;
        const labelWithRequired = field.label;
        const common = {
          control,
          name,
          label: labelWithRequired,
          required: field.required,
        } as const;

        let rendered: React.ReactNode = null;

        switch (field.type) {
          case 'TEXTAREA':
            rendered = <FormTextareaField {...common} placeholder="Type your answer here..." />;
            break;
          case 'NUMBER':
            rendered = <FormInputField {...common} type="number" placeholder="Enter a number" />;
            break;
          case 'SELECT':
            rendered =
              field.options && field.options.length > autocompleteThreshold ? (
                <FormAutoCompleteField {...common} options={field.options} />
              ) : (
                <FormSelectField
                  {...common}
                  options={(field.options || []).map((o) => ({ value: o, label: o }))}
                  placeholder="Select an option"
                />
              );
            break;
          case 'MULTI_SELECT':
            rendered = <FormCheckboxGroupField {...common} options={field.options || []} />;
            break;
          case 'DATE':
            rendered = <FormWheelDatePickerField {...common} placeholder="Select date" />;
            break;
          case 'IMAGE':
            rendered = (
              <FormImageUploadField
                {...common}
                uploadFolder={uploadFolder}
                aspect={field.cropAspect ?? 1}
                enableCrop={field.enableCrop ?? true}
                onUploadingChange={onUploadingChange}
              />
            );
            break;
          default: {
            // TEXT
            if (isPhoneField(field)) {
              rendered = (
                <FormPhoneField {...common} placeholder={`Enter ${field.label.toLowerCase()}`} />
              );
            } else {
              rendered = <FormInputField {...common} placeholder="Type your answer here..." />;
            }
          }
        }

        return (
          <div key={field.id}>
            {rendered}
            {renderFieldFooter?.(field)}
          </div>
        );
      })}
    </div>
  );
}
